import { useEffect, useState } from 'react'
import { format } from 'date-fns'
import {
  ClusterInterface,
  OrderReportResponse,
  Range,
} from '../../../../../common/types/interfaces'
import { getSalesApi } from '../../../../../salesApi/salesApi'

type Preset = 'today' | 'week' | 'month' | 'custom'

const getPresetRange = (preset: Exclude<Preset, 'custom'>): Range => {
  const now = new Date()

  if (preset === 'today') {
    const start = new Date(now)
    start.setHours(0, 0, 0, 0)
    const end = new Date(now)
    end.setHours(23, 59, 59, 999)
    return { from: start, to: end }
  }

  const from = new Date(now)
  if (preset === 'week') from.setDate(now.getDate() - 7)
  if (preset === 'month') from.setMonth(now.getMonth() - 1)
  return { from, to: now }
}

export const useOrderStatsItem = (cluster: ClusterInterface) => {
  const [preset, setPreset] = useState<Preset>('today')
  const [range, setRange] = useState<Range>(getPresetRange('today'))
  const [data, setData] = useState<OrderReportResponse | null>(null)
  const [loading, setLoading] = useState(false)

  const salesApi = getSalesApi()

  const changePreset = (p: Preset) => {
    setPreset(p)
    if (p !== 'custom') setRange(getPresetRange(p))
  }

  const onRangeChange = (value: string, first?: boolean) => {
    setPreset('custom')
    setRange((prev) => ({
      from: first ? new Date(value) : prev.from,
      to: first ? prev.to : new Date(value),
    }))
  }

  useEffect(() => {
    if (!range.from || !range.to) return
    let cancelled = false
    setLoading(true)

    salesApi
      .getOrderReport(cluster, {
        gte: format(range.from as Date, "yyyy-MM-dd'T'HH:mm:ss"),
        lte: format(range.to as Date, "yyyy-MM-dd'T'HH:mm:ss"),
      })
      .then((result) => {
        if (!cancelled) setData(result)
      })
      .catch(() => {
        if (!cancelled) setData(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [cluster.id, range.from, range.to])

  return { preset, changePreset, range, onRangeChange, data, loading }
}
